import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { logAudit, AUDIT_ACTIONS } from './auditService';
import { DEMO_CASES, DEMO_PHASES } from '../contexts/DemoContext';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

/**
 * Case Service
 * Handles treatment case management with phases and versioning
 */

export const createCase = async (practiceId, userId, patientId, caseData) => {
  if (IS_DEMO) {
    const id = 'demo-case-' + Date.now();
    return { id, ...caseData, patientId, status: 'active', currentVersion: 1, createdAt: new Date(), updatedAt: new Date() };
  }

  try {
    const batch = writeBatch(db);
    const caseRef = doc(collection(db, 'practices', practiceId, 'patients', patientId, 'cases'));
    const caseDoc = {
      ...caseData, patientId, practiceId,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      currentVersion: 1, status: 'active'
    };
    batch.set(caseRef, caseDoc);

    const versionRef = doc(collection(db, 'practices', practiceId, 'patients', patientId, 'cases', caseRef.id, 'versions'));
    batch.set(versionRef, {
      versionNumber: 1, ...caseData,
      createdBy: userId, createdAt: serverTimestamp(),
      notes: 'Initial case creation'
    });

    const phases = ['initial', 'postOrtho', 'provisional', 'final'];
    for (const phaseType of phases) {
      const phaseRef = doc(collection(db, 'practices', practiceId, 'patients', patientId, 'cases', caseRef.id, 'phases'));
      batch.set(phaseRef, {
        phaseType,
        status: phaseType === 'initial' ? 'in_progress' : 'not_started',
        photos: [], notes: '',
        createdAt: serverTimestamp(), updatedAt: serverTimestamp()
      });
    }

    await batch.commit();
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.CREATE,
      resourceType: 'case',
      resourceId: caseRef.id,
      details: { patientId, caseName: caseData.name }
    });
    return { id: caseRef.id, ...caseDoc };
  } catch (error) {
    console.error('Error creating case:', error);
    throw error;
  }
};

export const getCase = async (practiceId, userId, patientId, caseId) => {
  if (IS_DEMO) {
    const patientCases = DEMO_CASES[patientId] || [];
    const found = patientCases.find(c => c.id === caseId);
    if (!found) throw new Error('Case not found');
    return { ...found };
  }

  try {
    const docRef = doc(db, 'practices', practiceId, 'patients', patientId, 'cases', caseId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Case not found');
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.READ,
      resourceType: 'case',
      resourceId: caseId,
      details: { patientId }
    });
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error getting case:', error);
    throw error;
  }
};

export const getCasesForPatient = async (practiceId, userId, patientId) => {
  if (IS_DEMO) {
    return [...(DEMO_CASES[patientId] || [])];
  }

  try {
    const q = query(
      collection(db, 'practices', practiceId, 'patients', patientId, 'cases'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting cases:', error);
    throw error;
  }
};

export const getAllCases = async (practiceId, userId) => {
  if (IS_DEMO) {
    const allCases = [];
    Object.entries(DEMO_CASES).forEach(([patientId, cases]) => {
      cases.forEach(c => allCases.push({ ...c, patientId }));
    });
    return allCases.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  try {
    const patientsRef = collection(db, 'practices', practiceId, 'patients');
    const patientsSnap = await getDocs(patientsRef);
    const allCases = [];
    for (const patientDoc of patientsSnap.docs) {
      const casesRef = collection(db, 'practices', practiceId, 'patients', patientDoc.id, 'cases');
      const casesSnap = await getDocs(query(casesRef, orderBy('createdAt', 'desc')));
      casesSnap.docs.forEach(caseDoc => {
        allCases.push({ id: caseDoc.id, patientId: patientDoc.id, ...caseDoc.data() });
      });
    }
    return allCases.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });
  } catch (error) {
    console.error('Error getting all cases:', error);
    throw error;
  }
};

export const updateCase = async (practiceId, userId, patientId, caseId, caseData) => {
  if (IS_DEMO) {
    return { id: caseId, ...caseData, updatedAt: new Date() };
  }

  try {
    const updateData = { ...caseData, updatedAt: serverTimestamp() };
    const docRef = doc(db, 'practices', practiceId, 'patients', patientId, 'cases', caseId);
    await updateDoc(docRef, updateData);
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.UPDATE,
      resourceType: 'case',
      resourceId: caseId,
      details: { patientId, caseName: caseData.name }
    });
    return { id: caseId, ...updateData };
  } catch (error) {
    console.error('Error updating case:', error);
    throw error;
  }
};

export const createCaseVersion = async (practiceId, userId, patientId, caseId, versionData) => {
  if (IS_DEMO) {
    return { id: 'demo-version-' + Date.now(), versionNumber: 2, ...versionData };
  }

  try {
    const caseRef = doc(db, 'practices', practiceId, 'patients', patientId, 'cases', caseId);
    const caseSnap = await getDoc(caseRef);
    if (!caseSnap.exists()) throw new Error('Case not found');
    const currentCase = caseSnap.data();
    const newVersionNumber = (currentCase.currentVersion || 1) + 1;
    const batch = writeBatch(db);
    const versionRef = doc(collection(db, 'practices', practiceId, 'patients', patientId, 'cases', caseId, 'versions'));
    const versionDoc = {
      versionNumber: newVersionNumber, ...versionData,
      createdBy: userId, createdAt: serverTimestamp()
    };
    batch.set(versionRef, versionDoc);
    batch.update(caseRef, { currentVersion: newVersionNumber, updatedAt: serverTimestamp() });
    await batch.commit();
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.CREATE,
      resourceType: 'case_version',
      resourceId: versionRef.id,
      details: { caseId, versionNumber: newVersionNumber }
    });
    return { id: versionRef.id, ...versionDoc };
  } catch (error) {
    console.error('Error creating case version:', error);
    throw error;
  }
};

export const getCaseVersions = async (practiceId, patientId, caseId) => {
  if (IS_DEMO) {
    return [{ id: 'v1', versionNumber: 1, notes: 'Initial case creation', createdAt: new Date() }];
  }

  try {
    const q = query(
      collection(db, 'practices', practiceId, 'patients', patientId, 'cases', caseId, 'versions'),
      orderBy('versionNumber', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting case versions:', error);
    throw error;
  }
};

export const deleteCase = async (practiceId, userId, patientId, caseId, caseName) => {
  if (IS_DEMO) return true;

  try {
    const docRef = doc(db, 'practices', practiceId, 'patients', patientId, 'cases', caseId);
    await deleteDoc(docRef);
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.DELETE,
      resourceType: 'case',
      resourceId: caseId,
      details: { patientId, caseName }
    });
    return true;
  } catch (error) {
    console.error('Error deleting case:', error);
    throw error;
  }
};

export const getCasePhases = async (practiceId, patientId, caseId) => {
  if (IS_DEMO) {
    const phases = DEMO_PHASES[caseId] || [];
    const phaseOrder = ['initial', 'postOrtho', 'provisional', 'final'];
    return [...phases].sort((a, b) => phaseOrder.indexOf(a.phaseType) - phaseOrder.indexOf(b.phaseType));
  }

  try {
    const phasesRef = collection(db, 'practices', practiceId, 'patients', patientId, 'cases', caseId, 'phases');
    const querySnapshot = await getDocs(phasesRef);
    const phases = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const phaseOrder = ['initial', 'postOrtho', 'provisional', 'final'];
    return phases.sort((a, b) => phaseOrder.indexOf(a.phaseType) - phaseOrder.indexOf(b.phaseType));
  } catch (error) {
    console.error('Error getting phases:', error);
    throw error;
  }
};

export const updatePhase = async (practiceId, userId, patientId, caseId, phaseId, phaseData) => {
  if (IS_DEMO) {
    return { id: phaseId, ...phaseData, updatedAt: new Date() };
  }

  try {
    const updateData = { ...phaseData, updatedAt: serverTimestamp() };
    const phaseRef = doc(db, 'practices', practiceId, 'patients', patientId, 'cases', caseId, 'phases', phaseId);
    await updateDoc(phaseRef, updateData);
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.UPDATE,
      resourceType: 'phase',
      resourceId: phaseId,
      details: { caseId, phaseType: phaseData.phaseType }
    });
    return { id: phaseId, ...updateData };
  } catch (error) {
    console.error('Error updating phase:', error);
    throw error;
  }
};

export const getCasesCount = async (practiceId) => {
  if (IS_DEMO) {
    let count = 0;
    Object.values(DEMO_CASES).forEach(cases => { count += cases.length; });
    return count;
  }

  try {
    const cases = await getAllCases(practiceId, null);
    return cases.length;
  } catch (error) {
    console.error('Error getting cases count:', error);
    return 0;
  }
};

export default {
  createCase,
  getCase,
  getCasesForPatient,
  getAllCases,
  updateCase,
  createCaseVersion,
  getCaseVersions,
  deleteCase,
  getCasePhases,
  updatePhase,
  getCasesCount
};
